import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    IVuSettings,
    ChartSettings,
    getReportActionsState,
    ChartConfig,
    ChartAxes,
} from '../../Redux/VisualUnits/visualUnitStateSlice'
import { FoundationsEnums } from '../../enum'
import { getChartDisplayTypeForViewerType } from '../Charts/chartHelperFunctions'
import Dialog from '../../GenericComponents/dialog'
import { SwitchOrganizer } from './switchAxes'
import { RootState } from '../../Redux/store'
import { FoundationsConstants } from '../../FoundationsConstants'
import { VisualUnitApiDTO_VisualUnit } from '../../Interfaces/APIContracts/VisualUnitApiDTO_VisualUnit'
import { RunVisualUnitWithId_VisualUnit } from '../../Interfaces/APIContracts/RunVisualUnitWithId_VisualUnit'
import { RunVisualUnitWithId_VisualUnit_Extended } from '../../Interfaces/VisualUnit/RunVisualUnitWithId_VisualUnit'
import { useZustandVisualUnitStore } from './vuContext'

export const VUChartSetting = React.memo(
    (props: {
        vuId: number
        executeResult: RunVisualUnitWithId_VisualUnit
        isExplore: boolean
        isCollapsed: boolean
        chartConfig: ChartConfig
        updateChartConfig: (key: keyof ChartConfig, value: any) => void
    }) => {
        const {
            vuId,
            executeResult,
            isExplore,
            isCollapsed,
            chartConfig,
            updateChartConfig,
        } = props
        // Execute does not return table settings for charts and vice-versa, so we may have to
        // read settings from the vuDetails object if they are not present when changing VU types

        const vuDetails = useZustandVisualUnitStore((s) => s.vuState)

        if(!vuDetails){
            console.error("Dangling Setting Toolbar. No Visualunit is attached to it.")
            return <div>Unexpected error of not receiving Visualunit with toolbar</div>
        }

        const [localTableSettings, setLocalTableSettings] = useState<IVuSettings>(
            executeResult.tableSettings || vuDetails.TableSettings
        )
        const [localChartSettings, setLocalChartSettings] = useState<ChartSettings>(
            executeResult.chartSettings || vuDetails.ChartSettings
        )
        //
        // const [localViewerType, setLocalViewerType] = useState(executeResult.viewerType)
        // useEffect(()=>{
        //     setLocalViewerType(executeResult.viewerType)
        // },[executeResult.viewerType])
        const [selectedSettings, setSelectedSettings] = useState<{ [key: string]: boolean }>({});
        const [isDisableApply, setIsDisableApply] = useState<boolean>(true)
        const [zeroStartingValues, setZeroStartingValues] = useState(false)
        const [switchDialogOpen, setSwitchDialogOpen] = useState(false)
        const noMeasures = !vuDetails.MeasureList || vuDetails.MeasureList?.length < 1
        // const currentChartConfig = useSelector(selectChartConfigById(props.vuId || -1))
        const isSettingsOpen = useSelector(
            (state: RootState) => getReportActionsState(state)[vuId]?.isReportSettingOpen
        )

        useEffect(() => {
            if (executeResult.chartData !== undefined) {
                setZeroStartingValues(
                    executeResult.chartData.dataSeries !== undefined
                        ? checkForZeroStartingValues(executeResult.chartData.dataSeries)
                        : false
                )
            }
        }, [executeResult])

        const isTable = (viewerType: number) => {
            return (
                viewerType == FoundationsEnums.VisualUnitViewerType.Table ||
                viewerType == FoundationsEnums.VisualUnitViewerType.Unknown
            )
        }

        const updateViewerType = (viewerType: number) => {
            updateChartConfig('isDrawing', false)
            _updateViewerType(viewerType)
            // setLocalViewerType(viewerType)
        }

        const _updateViewerType = useZustandVisualUnitStore((s) => s.updateViewerType);
        const updateTableSetting = useZustandVisualUnitStore((s) => s.updateTableSetting);
        const updateChartSetting = useZustandVisualUnitStore((s) => s.updateChartSetting);
        const applyVUsettings =  () => {
            if (isTable(executeResult.viewerType)) {
                updateTableSetting(localTableSettings)
            } else {
                updateChartSetting(localChartSettings)
            }
            setSelectedSettings({});
            setIsDisableApply(true)
        }

        const changeTableItemStatus = (setting: keyof IVuSettings) => {
            setLocalTableSettings((prevSettings) => ({
                ...prevSettings,
                [setting]: !prevSettings[setting], // Toggle the setting value
                ApplySettings: true,
            }))
            setSelectedSettings(prev => ({
                ...prev,
                [setting]: !prev[setting]
            }));
            setIsDisableApply(false)
        }

        const changeChartItemStatus = (setting: keyof ChartSettings, key?: boolean | number) => {
            if (setting === 'ChartSortOrder') {
                if (key !== undefined && typeof key === 'number') {
                    setLocalChartSettings((prevSettings) => ({
                        ...prevSettings,
                        ChartSortOrder: key, // Toggle the setting value
                        ApplySettings: true,
                    }))
                    setSelectedSettings(prev => ({
                        ...prev,
                        'ChartSortOrder_1': key === FoundationsEnums.SortOrder.Ascending,
                        'ChartSortOrder_2': key === FoundationsEnums.SortOrder.Descending,
                        'ChartSortOrder_3': key === FoundationsEnums.SortOrder.System
                    }));
                }
            } else if (setting === 'ShowStacked') {
                if (key !== undefined && typeof key === 'boolean') {
                    setLocalChartSettings((prevSettings) => ({
                        ...prevSettings,
                        ShowStacked: key, // Toggle the setting value
                        ApplySettings: true,
                    }))
                    setSelectedSettings(prev => ({
                        ...prev,
                        'ShowStacked_stacked': key === true,
                        'ShowStacked_grouped': key === false
                    }));
                }
            } else {
                setLocalChartSettings((prevSettings) => ({
                    ...prevSettings,
                    [setting]: !prevSettings[setting], // Toggle the setting value
                    ApplySettings: true,
                }))
                setSelectedSettings(prev => ({
                    ...prev,
                    [setting]: !prev[setting]
                }));
            }
            setIsDisableApply(false)
        }

        const checkForZeroStartingValues = (dataSeries: any[]) => {
            for (let i = 0; i < dataSeries?.length; i++) {
                if (dataSeries[i].data[0] == 0) return true
            }
            return false
        }

        let percentTrendTooltip = ''
        if (executeResult.viewerType == FoundationsEnums.VisualUnitViewerType.Chart_Pie) {
            percentTrendTooltip = 'Percentage not available on Pie Charts'
        } else if (noMeasures) {
            percentTrendTooltip = 'Percentage not available with multiple measures'
        } else if (zeroStartingValues) {
            percentTrendTooltip = 'Percentage not available when a series starts with a 0 value'
        } else {
            percentTrendTooltip = 'Show Percentage Change From First Data Point'
        }

        // Let users disable percents if they were somehow already enabled on an invalid configuration

        const percentTrendDisabled =
            executeResult.viewerType == FoundationsEnums.VisualUnitViewerType.Chart_Pie ||
            noMeasures ||
            zeroStartingValues
        const viewerTypeName = getChartDisplayTypeForViewerType(executeResult.viewerType)
        const showPercentages = (executeResult as RunVisualUnitWithId_VisualUnit_Extended)
            .ShowTablePercentages
        return (
            <span style={{ position: 'relative' }}>
                {isTable(executeResult.viewerType) ? (
                    <div
                        id={'vu-chart-setting-list-' + vuId}
                        className="vu-chart-setting-list collapsible-content pl-3"
                        style={{
                            borderLeft: '1px solid grey',
                            borderRight: '1px solid grey',
                            maxHeight: isSettingsOpen && !isCollapsed ? '' : '0',
                        }}
                    >
                        <span className="setting-list-view-options">
                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Pie
                            ) ? (
                                <button
                                    title="Show report as a Pie Chart"
                                    className={`app-button feather icon-pie-chart ${viewerTypeName == 'pie' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Pie
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}
                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Bar
                            ) ? (
                                <button
                                    title="Show report as a Bar Chart"
                                    className={` app-button feather icon-align-left ${viewerTypeName == 'bar' || viewerTypeName == 'stackedBar' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Bar
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}
                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Line
                            ) ? (
                                <button
                                    title="Show report as a Line Chart"
                                    className={`app-button feather icon-trending-up ${viewerTypeName == 'line' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Line
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}
                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_ColumnGroup
                            ) ? (
                                <button
                                    title="Show report as a Column Chart"
                                    className={`app-button feather icon-bar-chart ${viewerTypeName == 'columnGroup' || viewerTypeName == '=stackedColumn' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_ColumnGroup
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}
                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Scatter
                            ) ? (
                                <button
                                    title="Show report as a Scatter Plot"
                                    className={`app-button feather icon-more-horizontal ${viewerTypeName == 'scatter' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Scatter
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}
                            <button
                                title="Show report as a Table"
                                className={`app-button feather icon-grid ${isTable(executeResult.viewerType) ? 'active' : ''}`}
                                onClick={() => {
                                    if (!isTable(executeResult.viewerType))
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Table
                                        )
                                    return false
                                }}
                            ></button>
                        </span>
                        <span className="setting-list-data-options">
                            <span style={{ paddingLeft: '5px' }}>
                                <b>Display: </b>
                                <button
                                    title="Show Zero Values in Table"
                                    id={`vu_setting_chartzerovalue_${vuId}'`}
                                    className={`setting-item ${
                                        selectedSettings['ShowTableZeroValues'] 
                                            ? 'setting-item-selected'
                                            : localTableSettings.ShowTableZeroValues 
                                                ? 'setting-item-current' 
                                                : 'setting-item-allowed'
                                    }`}
                                    onClick={() => changeTableItemStatus('ShowTableZeroValues')}
                                >
                                    Zero Values
                                </button>
                                <button
                                    title={`${!showPercentages ? 'Percentages are not available when series contains zero values' : 'Show Percentages in Table'}`}
                                    id={`vu_setting_percentages_${vuId}'`}
                                    className={`setting-item ${
                                        !showPercentages 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ShowTablePercentages']
                                                ? 'setting-item-selected'
                                                : localTableSettings.ShowTablePercentages 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                    }`}
                                    onClick={() =>
                                        showPercentages &&
                                        changeTableItemStatus('ShowTablePercentages')
                                    }
                                >
                                    Percentages
                                </button>
                                <button
                                    title={`${vuDetails.XAxisList?.length == 0 ? 'No Row Organizer is Selected' : 'Show Grand Total Row'}`}
                                    id={`vu_setting_totaltyper_${vuId}'`}
                                    className={`setting-item  ${
                                        vuDetails.XAxisList?.length == 0 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ShowGrandTotalRow']
                                                ? 'setting-item-selected'
                                                : localTableSettings.ShowGrandTotalRow 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                    }`}
                                    onClick={() =>
                                        vuDetails.XAxisList?.length > 0 &&
                                        changeTableItemStatus('ShowGrandTotalRow')
                                    }
                                >
                                    Grand Row Total
                                </button>
                                <button
                                    title={`${vuDetails.YAxisList?.length == 0 ? 'No Column Organizer is Selected' : 'Show Grand Total Column'}`}
                                    id={`vu_setting_totaltypec_${vuId}'`}
                                    className={`setting-item ${
                                        vuDetails.YAxisList?.length == 0 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ShowGrandTotalCol']
                                                ? 'setting-item-selected'
                                                : localTableSettings.ShowGrandTotalCol
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                    }`}
                                    onClick={() =>
                                        vuDetails.YAxisList?.length > 0 &&
                                        changeTableItemStatus('ShowGrandTotalCol')
                                    }
                                >
                                    Grand Column Total
                                </button>
                                <button
                                    title="Show Empty Rows"
                                    id={`vu_setting_nullorempty_${vuId}'`}
                                    className={`setting-item ${
                                        selectedSettings['ShowNullOrEmpty']
                                            ? 'setting-item-selected'
                                            : localTableSettings.ShowNullOrEmpty 
                                                ? 'setting-item-current' 
                                                : 'setting-item-allowed'
                                    }`}
                                    onClick={() => changeTableItemStatus('ShowNullOrEmpty')}
                                >
                                    Empty Rows
                                </button>
                                <button
                                    title={`${vuDetails.XAxisList?.length != 2 ? 'No Repeated rows' : 'Show Repeated Rows'}`}
                                    id={`vu_setting_repeatedrows_${vuId}'`}
                                    className={`setting-item ${
                                        vuDetails.XAxisList?.length != 2 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ShowRepeatedRows'] 
                                                ? 'setting-item-selected'
                                                : localTableSettings.ShowRepeatedRows 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                    }`}
                                    onClick={() =>
                                        vuDetails.XAxisList?.length === 2 &&
                                        changeTableItemStatus('ShowRepeatedRows')
                                    }
                                >
                                    Repeated Rows
                                </button>
                                <button
                                    title={`${vuDetails.XAxisList?.length === 2 && vuDetails.YAxisList?.length === 0 ? 'Show Sub Totals' : 'No Sub totals for this data'}`}
                                    id={`vu_setting_subtotals_${vuId}`}
                                    className={`setting-item ${
                                        vuDetails.XAxisList?.length === 2 && vuDetails.YAxisList?.length === 0 ? (localTableSettings.ShowSubTotals ? 'setting-item-current' : 'setting-item-allowed') : 'setting-item-disabled'}`}
                                    onClick={() =>
                                        vuDetails.XAxisList?.length === 2 &&
                                        vuDetails.YAxisList?.length === 0 &&
                                        changeTableItemStatus('ShowSubTotals')
                                    }
                                >
                                    Sub Totals
                                </button>
                            </span>
                            <span className="apply-button-container pl-2">
                                <button
                                    title={
                                        !isDisableApply
                                            ? 'Apply selected settings'
                                            : 'You must select new display settings to apply them'
                                    }
                                    id={`settings_bar_apply_button_${vuId}`}
                                    className={
                                        'app-button mb-2 ' + (!isDisableApply ? '' : 'disabled')
                                    }
                                    style={{ paddingLeft: '5px' }}
                                    onClick={() => {
                                        if (isDisableApply) return
                                        applyVUsettings()
                                    }}
                                    disabled={isDisableApply}
                                >
                                    Apply
                                </button>
                            </span>
                        </span>
                    </div>
                ) : (
                    <div
                        id={'vu-chart-setting-list-' + vuId}
                        className="vu-chart-setting-list collapsible-content pl-3"
                        style={{
                            borderLeft: '1px solid grey',
                            borderRight: '1px solid grey',
                            maxHeight: isSettingsOpen && !isCollapsed ? '' : '0',
                        }}
                    >
                        {/* TODO !! bind actions for remaining buttons */}
                        <span className="setting-list-view-options">
                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Pie
                            ) ? (
                                <button
                                    title="Show report as a Pie Chart"
                                    className={`app-button feather icon-pie-chart ${viewerTypeName == 'pie' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Pie
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}

                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Bar
                            ) ? (
                                <button
                                    title="Show report as a Bar Chart"
                                    className={`app-button feather icon-align-left ${viewerTypeName == 'bar' || viewerTypeName == 'stackedBar' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Bar
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}

                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Line
                            ) ? (
                                <button
                                    title="Show report as a Line Chart"
                                    className={`app-button feather icon-trending-up ${viewerTypeName == 'line' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Line
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}

                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_ColumnGroup
                            ) ? (
                                <button
                                    title="Show report as a Column Chart"
                                    className={`app-button feather icon-bar-chart ${viewerTypeName == 'columnGroup' || viewerTypeName == 'stackedColumn' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_ColumnGroup
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}

                            {executeResult.availableChartTypes?.includes(
                                FoundationsEnums.VisualUnitViewerType.Chart_Scatter
                            ) ? (
                                <button
                                    title="Show report as a Scatter Plot"
                                    className={`app-button feather icon-more-horizontal ${viewerTypeName == 'scatter' ? 'active' : ''}`}
                                    onClick={() => {
                                        updateViewerType(
                                            FoundationsEnums.VisualUnitViewerType.Chart_Scatter
                                        )
                                    }}
                                ></button>
                            ) : (
                                ``
                            )}
                            <button
                                title="Show report as a Table"
                                className={`app-button feather icon-grid ${viewerTypeName == 'table' ? 'active' : ''}`}
                                onClick={() => {
                                    updateViewerType(FoundationsEnums.VisualUnitViewerType.Table)
                                }}
                            ></button>
                            <span className="py-0">
                                {/* TODO !! handle full screen, 2d/3d (also api call for this), and legend using redux store!, */}
                                {vuDetails.XAxisList?.length + vuDetails.YAxisList?.length > 1 ||
                                vuDetails.MeasureList?.length > 1 ||
                                viewerTypeName == 'pie' ? (
                                    <button
                                        title={
                                            chartConfig?.showLegend ? 'Hide Legend' : 'Show Legend'
                                        }
                                        id={`toggle_legend_button_${vuId}`}
                                        className={`chart-button app-button ${chartConfig?.showLegend ? 'active' : ''}`}
                                        onClick={() => {
                                            updateChartConfig(
                                                'showLegend',
                                                !chartConfig?.showLegend
                                            )
                                        }}
                                    >
                                        Legend
                                    </button>
                                ) : (
                                    <></>
                                )}
                                <button
                                    title={
                                        chartConfig?.isDrawing
                                            ? 'Cancel Drawing'
                                            : 'Doodle on Charts'
                                    }
                                    id={`doodle_button_${vuId}`}
                                    className="chart-button app-button"
                                    style={{ width: ' 35px' }}
                                    onClick={() => {
                                        updateChartConfig('isDrawing', !chartConfig?.isDrawing)
                                    }}
                                >
                                    {chartConfig?.isDrawing ? 'x' : '✎'}
                                </button>
                            </span>
                            {/* TODO !! store values that change chart config in server side as well as local, like 3d view and legend */}
                            <span
                                className="view_types py-0"
                                style={{ overflow: 'visible', display: 'inlineBlock' }}
                            >
                                {vuDetails.XAxisList?.length + vuDetails.YAxisList?.length > 1 ||
                                viewerTypeName == 'scatter' ? (
                                    <>
                                        <button
                                            title="Change chart axes"
                                            id={`link_dashboard_vu_switch_${vuId}`}
                                            className="app-button edit-option"
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                setSwitchDialogOpen(true)
                                            }}
                                        >
                                            Switch
                                        </button>
                                        <Dialog
                                            isOpen={switchDialogOpen}
                                            onClose={() => setSwitchDialogOpen(false)} // Close dialog
                                            className="ui-front ui-dialog-buttons"
                                            title="Switch Axes"
                                            width={'480px'}
                                            closeText="Cancel"
                                            buttons={[
                                                {
                                                    label: 'Cancel',
                                                    className:
                                                        'ui-button app-button negative cancel_vu_param',
                                                    onClick: () => setSwitchDialogOpen(false), // Retain the original method for cancel
                                                },
                                            ]}
                                        >
                                            <SwitchOrganizer
                                                visualUnitId={vuId}
                                                viewerType={executeResult.viewerType}
                                                vuDetail={vuDetails}
                                                closeWindow={() => setSwitchDialogOpen(false)}
                                                vuChartSwitchCurr={executeResult.chartSwitch}
                                            />
                                        </Dialog>
                                    </>
                                ) : (
                                    ``
                                )}
                                {isExplore ? (
                                    <></>
                                ) : (
                                    <button
                                        title="Show Full Screen Chart"
                                        className="app-button non-edit-option"
                                        onClick={() => {
                                            updateChartConfig(
                                                'isFullScreen',
                                                !chartConfig?.isFullScreen
                                            )
                                        }}
                                    >
                                        Full Screen
                                    </button>
                                )}
                                {isExplore ? (
                                    <></>
                                ) : executeResult.enable3dView ? (
                                    <button
                                        title={`Show ${chartConfig?.enable3dView ? '2D' : '3D'} Chart`}
                                        className="app-button non-edit-option"
                                        onClick={() => {
                                            updateChartConfig(
                                                'enable3dView',
                                                !chartConfig?.enable3dView
                                            )
                                        }}
                                    >
                                        {chartConfig?.enable3dView ? '2D' : '3D'}
                                    </button>
                                ) : (
                                    <></>
                                )}
                            </span>
                        </span>
                        <span className="setting-list-data-options">
                            <span style={{ paddingLeft: '5px' }}>
                                <b>Display: </b>
                                <button
                                    title={percentTrendTooltip}
                                    id={`vu_setting_changetrend_${vuId}`}
                                    className={`setting-item ${
                                        percentTrendDisabled 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['showPercentageTrend'] 
                                                ? 'setting-item-selected'
                                                : localChartSettings?.showPercentageTrend 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'}
                                    `}
                                    onClick={() => changeChartItemStatus('showPercentageTrend')}
                                >
                                    % Change
                                </button>
                                <button
                                    title={
                                        viewerTypeName == 'pie'
                                            ? 'Summation not available on Pie Charts'
                                            : noMeasures
                                              ? 'Summation not available with multiple measures'
                                              : 'Show Chart Summation Line'
                                    }
                                    id={`vu_setting_summation_${vuId}`}
                                    className={`setting-item  ${
                                        viewerTypeName == 'pie' || noMeasures 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ShowChartSummation']
                                                ? 'setting-item-selected'
                                                : localChartSettings?.ShowChartSummation 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'}`}
                                    onClick={() => changeChartItemStatus('ShowChartSummation')}
                                >
                                    Summation
                                </button>
                                <button
                                    title="Show Data Labels on Chart"
                                    id={`vu_setting_datalabel_${vuId}`}
                                    className={`setting-item ${
                                        selectedSettings['ShowChartDataLabel']
                                            ? 'setting-item-selected' 
                                            : localChartSettings?.ShowChartDataLabel 
                                                ? 'setting-item-current' 
                                                : 'setting-item-allowed'
                                    }`}
                                    onClick={() => changeChartItemStatus('ShowChartDataLabel')}
                                >
                                    Data Labels
                                </button>
                                <button
                                    title="Show Animation on Chart"
                                    id={`vu_setting_animation_${vuId}`}
                                    className={`setting-item ${
                                        selectedSettings['ShowChartAnimation']
                                            ? 'setting-item-selected' 
                                            : localChartSettings?.ShowChartAnimation 
                                                ? 'setting-item-current' 
                                                : 'setting-item-allowed'
                                    }`}
                                    onClick={() => changeChartItemStatus('ShowChartAnimation')}
                                >
                                    Animation
                                </button>
                                <button
                                    title="Show Zero Values in Chart"
                                    id={`vu_setting_chartzerovalue_${vuId}`}
                                    className={`setting-item ${
                                        selectedSettings['ShowChartZeroValue'] 
                                            ? 'setting-item-selected'
                                            : localChartSettings?.ShowChartZeroValue
                                                ? 'setting-item-current' 
                                                : 'setting-item-allowed'
                                    }`}
                                    onClick={() => changeChartItemStatus('ShowChartZeroValue')}
                                >
                                    Zero Values
                                </button>
                            </span>
                            <span
                                id={`vu_setting_sortorder_${vuId}`}
                                style={{ paddingLeft: '5px' }}
                            >
                                <b>Sort Order: </b>
                                <button
                                    id={`vu_setting_sortorder_${vuId}_ascending`}
                                    title={`${viewerTypeName == 'Pie' ? 'Order not applicable on pie charts' : 'Set order to be ascending'}`}
                                    className={`setting-item sorting ${
                                        viewerTypeName == 'pie' 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ChartSortOrder_1']
                                                ? 'setting-item-selected original-selection'
                                                : localChartSettings?.ChartSortOrder == 1 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                    }`}
                                    onClick={
                                        viewerTypeName !== 'pie'
                                            ? () => {
                                                  changeChartItemStatus(
                                                      'ChartSortOrder',
                                                      FoundationsEnums.SortOrder.Ascending
                                                  )
                                              }
                                            : () => {}
                                    }
                                >
                                    asc
                                </button>
                                <button
                                    id={`vu_setting_sortorder_${vuId}_descending`}
                                    title={`${viewerTypeName == 'Pie' ? 'Order not applicable on pie charts' : 'Set order to be descending'}`}
                                    className={`setting-item sorting ${
                                        viewerTypeName == 'pie' 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ChartSortOrder_2']
                                                ? 'setting-item-selected original-selection'
                                                : localChartSettings?.ChartSortOrder == 2 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                    }`}
                                    onClick={
                                        viewerTypeName !== 'pie'
                                            ? () => {
                                                  changeChartItemStatus(
                                                      'ChartSortOrder',
                                                      FoundationsEnums.SortOrder.Descending
                                                  )
                                              }
                                            : () => {}
                                    }
                                >
                                    desc
                                </button>
                                <button
                                    id={`vu_setting_sortorder_${vuId}_system`}
                                    title={`${viewerTypeName == 'Pie' ? 'Order not applicable on pie charts' : 'Set order to default'}`}
                                    className={`setting-item sorting ${
                                        viewerTypeName == 'pie' 
                                            ? 'setting-item-disabled' 
                                            : selectedSettings['ChartSortOrder_3']
                                                ? 'setting-item-selected original-selection'
                                                : localChartSettings?.ChartSortOrder == 3 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                    }`}
                                    onClick={
                                        viewerTypeName !== 'pie'
                                            ? () => {
                                                  changeChartItemStatus(
                                                      'ChartSortOrder',
                                                      FoundationsEnums.SortOrder.System
                                                  )
                                              }
                                            : () => {}
                                    }
                                >
                                    system
                                </button>
                            </span>
                            {vuDetails.XAxisList?.length + vuDetails.YAxisList?.length > 1 &&
                            (viewerTypeName == 'bar' ||
                                viewerTypeName == 'stackedBar' ||
                                viewerTypeName == 'columnGroup' ||
                                viewerTypeName == 'stackedColumn') ? (
                                <span
                                    id={`vu_setting_stackedgrouped_${vuId}`}
                                    style={{ paddingLeft: '5px' }}
                                >
                                    <b>Grouping: </b>
                                    <button
                                        id={`vu_setting_stackedgrouped_${vuId}_yes`}
                                        title="Show chart stacked"
                                        className={`setting-item grouping ${
                                            selectedSettings['ShowStacked_stacked']
                                                ? 'setting-item-selected'
                                                : localChartSettings?.ShowStacked 
                                                    ? 'setting-item-current'
                                                    : 'setting-item-allowed'
                                        }`}
                                        onClick={() => {
                                            changeChartItemStatus('ShowStacked', true)
                                        }}
                                    >
                                        Stacked
                                    </button>
                                    <button
                                        id={`vu_setting_stackedgrouped_${vuId}_no`}
                                        title="Show chart grouped"
                                        className={`setting-item grouping ${
                                            selectedSettings['ShowStacked_grouped'] 
                                                ? 'setting-item-selected'
                                                : !localChartSettings?.ShowStacked 
                                                    ? 'setting-item-current' 
                                                    : 'setting-item-allowed'
                                        }`}
                                        onClick={() => {
                                            changeChartItemStatus('ShowStacked', false)
                                        }}
                                    >
                                        Grouped
                                    </button>
                                </span>
                            ) : (
                                <span
                                    id={`vu_setting_stackedgrouped_${vuId}`}
                                    style={{ paddingLeft: '5px' }}
                                >
                                    <b>Grouping: </b>
                                    <button
                                        id={`vu_setting_stackedgrouped_${vuId}_yes`}
                                        title="Chart grouping is only available on bar and column charts with multiple organizers"
                                        className="setting-item setting-item-disabled grouping "
                                    >
                                        Stacked
                                    </button>
                                    <button
                                        id={`vu_setting_stackedgrouped_${vuId}_no`}
                                        title="Chart grouping is only available on bar and column charts with multiple organizers"
                                        className="setting-item setting-item-disabled grouping"
                                    >
                                        Grouped
                                    </button>
                                </span>
                            )}
                            <span className="apply-button-container pl-2">
                                {/* Charts settings */}
                                <button
                                    title={
                                        !isDisableApply
                                            ? 'Apply selected settings'
                                            : 'You must select new display settings to apply them'
                                    }
                                    id={`settings_bar_apply_button_${vuId}`}
                                    className={
                                        'app-button mb-2 ' + (!isDisableApply ? '' : 'disabled')
                                    }
                                    style={{ paddingLeft: '5px' }}
                                    onClick={() => {
                                        if (isDisableApply) return
                                        applyVUsettings()
                                    }}
                                    disabled={isDisableApply}
                                >
                                    Apply
                                </button>
                            </span>
                        </span>
                    </div>
                )}
            </span>
        )
    }
)
